import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

import { fetchMe } from '@/api/services/meService';
import type { MeResponse } from '@/api/types/MeResponse';
import type { Role } from '@/constants/roles';

import { loggedOut } from './actions';
import type { RootState } from './index';

// `loggedOut` is an ABSORBING state, distinct from `idle` on purpose: `idle`
// means "not fetched yet, please fetch", while `loggedOut` means "the session
// was deliberately ended, never fetch again on this page". Collapsing the two
// is exactly the bug this exists for — see the `loggedOut` case below.
export type UserStatus = 'idle' | 'loading' | 'ready' | 'error' | 'loggedOut';

// Why the /api/me fetch failed. The distinction is load-bearing for AuthGate
// (LOCKED plan 2026-08-24-auth-session-recovery, W1/D): an `auth` failure is a
// dead session and belongs to the re-auth coordinator (never an error pane),
// while a `transient` one (offline, 5xx, api still booting) deserves a couple
// of silent auto-retries before we show anything at all. Only `fatal` reaches
// the user as an error pane.
export type UserErrorKind = 'auth' | 'transient' | 'fatal';

export interface UserErrorInfo {
  kind: UserErrorKind;
  status: number | null;
  message: string;
}

export interface UserState {
  profile: MeResponse | null;
  status: UserStatus;
  error: string | null;
  errorKind: UserErrorKind | null;
  errorStatus: number | null;
}

const initialState: UserState = {
  profile: null,
  status: 'idle',
  error: null,
  errorKind: null,
  errorStatus: null,
};

// Classifies an /api/me failure. `status === undefined` means the request never
// got a response (offline / CORS / aborted) — transient by definition.
const classify = (e: unknown): UserErrorInfo => {
  const message = e instanceof Error ? e.message : 'unknown';
  if (axios.isAxiosError(e)) {
    const status = e.response?.status ?? null;
    if (status === null) return { kind: 'transient', status, message };
    if (status === 401) return { kind: 'auth', status, message };
    if (status >= 500) return { kind: 'transient', status, message };
    return { kind: 'fatal', status, message };
  }
  return { kind: 'fatal', status: null, message };
};

// Wraps the /api/me call. Rejects with a structured value (patterns.md: a thunk
// whose caller must distinguish error classes MUST `rejectWithValue({...})` —
// the default `miniSerializeError` throws the axios body away).
export const loadMe = createAsyncThunk<MeResponse, void, { rejectValue: UserErrorInfo }>(
  'user/loadMe',
  async (_arg, { rejectWithValue }) => {
    try {
      return await fetchMe();
    } catch (e) {
      return rejectWithValue(classify(e));
    }
  },
);

const userSlice = createSlice({
  name: 'user',
  initialState,
  // The DATA wipe on logout happens at the root reducer (`store/index.ts`), not
  // here — see the `loggedOut` case in `extraReducers` for the one thing this
  // slice still has to say about it.
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadMe.pending, (state) => {
        // The latch has to hold from the FIRST phase, not just at settle time:
        // letting `pending` write 'loading' would clear `loggedOut` and hand the
        // subsequent `fulfilled` a state it happily writes into.
        if (state.status === 'loggedOut') return;
        state.status = 'loading';
        state.error = null;
        state.errorKind = null;
        state.errorStatus = null;
      })
      .addCase(loadMe.fulfilled, (state, action: PayloadAction<MeResponse>) => {
        // A request that was already in the air when the user logged out must
        // NOT resurrect them. `loggedOut` is absorbing; nothing reopens it.
        if (state.status === 'loggedOut') return;
        state.status = 'ready';
        state.profile = action.payload;
        state.error = null;
        state.errorKind = null;
        state.errorStatus = null;
      })
      .addCase(loadMe.rejected, (state, action) => {
        // Same for a late rejection: after logout there is nobody to show an
        // error pane to, and flipping out of `loggedOut` would re-open the door.
        if (state.status === 'loggedOut') return;
        state.status = 'error';
        // An `auth` failure keeps the previously loaded profile: the coordinator
        // is about to bounce us through Keycloak and back, and wiping the
        // profile here would tear the shell down mid-recovery (and would drop
        // the unsaved-work prompt's page behind it).
        if (action.payload?.kind !== 'auth') state.profile = null;
        state.error = action.payload?.message ?? 'unknown';
        state.errorKind = action.payload?.kind ?? 'fatal';
        state.errorStatus = action.payload?.status ?? null;
      })
      // Runs AFTER the root reducer has already reset this slice to its initial
      // state (a slice's `extraReducers` still fire on the `undefined` → initial
      // fallback), so this only has to latch the one bit the reset cannot carry.
      //
      // Why the latch is needed at all: `Topbar` dispatches `loggedOut()` BEFORE
      // `signoutRedirect()`, so for the moment in between `auth.isAuthenticated`
      // is still true. Reset to plain `idle`, that satisfies AuthGate's
      // "authenticated but not fetched" effect, which fires a fresh `loadMe` —
      // and on localhost `/api/me` beats the signout navigation almost every
      // time. The profile comes back, `status` returns to `ready`, and the
      // bfcache-Back precondition (`!isAuthenticated && everAuthenticated &&
      // ready`) is fully armed: precisely the state this whole mechanism exists
      // to prevent.
      .addCase(loggedOut, (state) => {
        state.status = 'loggedOut';
      });
  },
});

export const userReducer = userSlice.reducer;

// ----- Selectors ---------------------------------------------------------
// Forward-looking helpers (`resetUser`, `selectHasRole`, `selectHasAnyRole`)
// are intentionally NOT exported until a feature genuinely needs them —
// dead-API drift is a real cost; reintroduce on first real caller. (Logout is
// NOT such a caller: it resets every slice at once via `auth/loggedOut`.)
export const selectUser = (state: RootState): MeResponse | null => state.user.profile;
export const selectUserStatus = (state: RootState): UserStatus => state.user.status;
export const selectUserError = (state: RootState): string | null => state.user.error;
export const selectUserErrorKind = (state: RootState): UserErrorKind | null =>
  state.user.errorKind;
export const selectUserErrorStatus = (state: RootState): number | null =>
  state.user.errorStatus;
export const selectUserRoles = (state: RootState): readonly Role[] =>
  state.user.profile?.roles ?? [];

// The logged-in user's Keycloak `sub`, or `null` when not loaded. SINGLE source
// of truth for the app-wide "is this jízda mine?" notion (my-jizdy yellow
// highlight): every site compares against this — never an ad-hoc sub read.
export const selectMySub = (state: RootState): string | null =>
  state.user.profile?.sub ?? null;

// "Mine" = pure sub-match, role-agnostic (a backoffice user who is the assignee
// is also "mine"). When `mySub` is not loaded (`null`) → never mine. Curried so
// a component can `useAppSelector(selectIsMine(order.assignedDispatcherSub))`.
export const selectIsMine =
  (sub: string | null) =>
  (state: RootState): boolean => {
    const mySub = state.user.profile?.sub ?? null;
    return mySub !== null && sub === mySub;
  };
